/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import { api } from "../lib/api";

type User = { publicId: string; name: string; email: string; roles: string[]; mustChangePassword?: boolean };
type AuthValue = {
  user: User | null;
  accessToken: string | null;
  login: (email: string, password: string) => Promise<void>;
  refresh: () => Promise<boolean>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const value = useMemo<AuthValue>(() => ({
    user,
    accessToken,
    login: async (email, password) => {
      const result = await api<{ accessToken: string; user: User }>("/auth/login", {
        method: "POST", body: JSON.stringify({ email, password })
      });
      setAccessToken(result.accessToken); setUser(result.user);
    },
    refresh: async () => {
      try {
        const result = await api<{ accessToken: string; user: User }>("/auth/refresh", { method: "POST" });
        setAccessToken(result.accessToken); setUser(result.user); return true;
      } catch { setAccessToken(null); setUser(null); return false; }
    },
    logout: async () => {
      await api("/auth/logout", { method: "POST" }).catch(() => undefined);
      setAccessToken(null); setUser(null);
    }
  }), [accessToken, user]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used within AuthProvider");
  return value;
}
